"use client"

import { useCallback, useEffect, useRef, useState } from "react"
import { Check, Link2 } from "lucide-react"
import { TikTokIcon, XIcon } from "@/components/icons/social"

/**
 * Share row for a help guide.
 *
 * An honest one. X, Facebook and LinkedIn expose real web share intents, so
 * those buttons open a prefilled composer. Instagram and TikTok DO NOT — no
 * URL can prefill a post on either, and pretending otherwise means a button
 * that opens a logged-out home page. Instead they:
 *
 *   • call `navigator.share()` where it exists (phones, most tablets), which
 *     opens the OS sheet — and that sheet really does list Instagram and
 *     TikTok, so the share completes in the app the reader expected;
 *   • copy the link otherwise, and say so, because pasting into a story or a
 *     bio is the actual desktop workflow for both.
 *
 * Links are always the canonical production URL, never window.location — a
 * guide shared from a preview deploy or localhost has to point at boothapp.co.
 */

const SITE = "https://boothapp.co"

type Channel = {
  id: string
  label: string
  /** Builds an intent URL, or null when the network has no share endpoint. */
  intent: ((url: string, text: string) => string) | null
  icon: (props: { className?: string }) => React.ReactElement
}

/* Brand marks are hand-drawn here: lucide dropped its brand set, and pulling a
   logo package in for five 14px glyphs isn't worth the dependency. */


function InstagramIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden>
      <rect x="2.75" y="2.75" width="18.5" height="18.5" rx="5.25" stroke="currentColor" strokeWidth="1.7" />
      <circle cx="12" cy="12" r="4.15" stroke="currentColor" strokeWidth="1.7" />
      <circle cx="17.45" cy="6.6" r="1.2" fill="currentColor" />
    </svg>
  )
}


function FacebookIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
      <path d="M22 12.06C22 6.5 17.52 2 12 2S2 6.5 2 12.06c0 5.02 3.66 9.18 8.44 9.94v-7.03H7.9v-2.91h2.54V9.85c0-2.52 1.5-3.91 3.77-3.91 1.09 0 2.24.2 2.24.2v2.46h-1.26c-1.24 0-1.63.78-1.63 1.57v1.89h2.78l-.45 2.91h-2.33V22c4.78-.76 8.44-4.92 8.44-9.94z" />
    </svg>
  )
}

function LinkedInIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
      <path d="M4.98 3.5a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5zM3 9h4v12H3zM9 9h3.8v1.71h.05c.53-.95 1.83-1.96 3.77-1.96 4.03 0 4.78 2.5 4.78 5.76V21h-4v-5.55c0-1.32-.03-3.03-1.9-3.03-1.9 0-2.2 1.44-2.2 2.93V21H9z" />
    </svg>
  )
}

const CHANNELS: Channel[] = [
  {
    id: "x",
    label: "Share on X",
    intent: (url, text) => `https://x.com/intent/post?url=${url}&text=${text}`,
    icon: XIcon,
  },
  {
    id: "facebook",
    label: "Share on Facebook",
    intent: (url) => `https://www.facebook.com/sharer/sharer.php?u=${url}`,
    icon: FacebookIcon,
  },
  {
    id: "linkedin",
    label: "Share on LinkedIn",
    intent: (url) => `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
    icon: LinkedInIcon,
  },
  // No share endpoint exists for either of these — they take the native
  // sheet on mobile and copy-to-clipboard everywhere else.
  { id: "instagram", label: "Share to Instagram", intent: null, icon: InstagramIcon },
  { id: "tiktok", label: "Share to TikTok", intent: null, icon: TikTokIcon },
]

export function HelpShare({
  slug,
  title,
  summary,
  accent,
}: {
  slug: string
  title: string
  summary: string
  /** "R G B" triple — buttons take the guide's hue on hover. */
  accent: string
}) {
  const [copied, setCopied] = useState<string | null>(null)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current)
  }, [])

  const url = `${SITE}/help/${slug}`

  const flash = useCallback((id: string) => {
    setCopied(id)
    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(() => setCopied(null), 2200)
  }, [])

  const copy = useCallback(
    async (id: string) => {
      try {
        await navigator.clipboard.writeText(url)
        flash(id)
      } catch {
        // Clipboard is permission-gated and throws on insecure origins. A
        // prompt is a worse experience than a no-op but better than silence.
        window.prompt("Copy this link", url)
      }
    },
    [url, flash],
  )

  const open = useCallback((href: string) => {
    // Popup rather than a new tab: the composer closes itself on post and
    // hands the reader straight back to the guide.
    window.open(href, "_blank", "noopener,noreferrer,width=600,height=640")
  }, [])

  const share = useCallback(
    async (channel: Channel) => {
      if (channel.intent) {
        open(channel.intent(encodeURIComponent(url), encodeURIComponent(title)))
        return
      }
      // Native sheet first — it's the only path that actually reaches the
      // Instagram and TikTok apps.
      if (typeof navigator !== "undefined" && navigator.share) {
        try {
          await navigator.share({ title, text: summary, url })
          return
        } catch {
          // Dismissed, or the sheet refused. Fall through to the clipboard.
        }
      }
      await copy(channel.id)
    },
    [url, title, summary, open, copy],
  )

  return (
    <div className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-3">
      <span className="text-[12.5px]" style={{ color: "var(--pg-muted)" }}>
        {copied ? "Link copied — paste it in your post" : "Share this guide"}
      </span>

      <div className="flex items-center gap-2">
        {CHANNELS.map((c) => {
          const Icon = c.icon
          const isCopied = copied === c.id
          return (
            <button
              key={c.id}
              type="button"
              onClick={() => share(c)}
              aria-label={c.label}
              title={c.label}
              className="group/share grid h-9 w-9 place-items-center rounded-full transition-transform duration-200 hover:-translate-y-0.5 active:scale-95"
              style={{
                background: isCopied ? `rgb(${accent} / 0.16)` : "var(--pg-card)",
                border: `1px solid ${isCopied ? `rgb(${accent} / 0.45)` : "var(--pg-card-edge)"}`,
                color: isCopied ? `rgb(${accent})` : "var(--pg-muted)",
                transition: "background 260ms ease, border-color 260ms ease, color 260ms ease, transform 200ms ease",
              }}
            >
              {isCopied ? <Check className="h-4 w-4" strokeWidth={2.4} /> : <Icon className="h-[15px] w-[15px]" />}
            </button>
          )
        })}

        <button
          type="button"
          onClick={() => copy("link")}
          aria-label="Copy link"
          title="Copy link"
          className="grid h-9 w-9 place-items-center rounded-full transition-transform duration-200 hover:-translate-y-0.5 active:scale-95"
          style={{
            background: copied === "link" ? `rgb(${accent} / 0.16)` : "var(--pg-card)",
            border: `1px solid ${copied === "link" ? `rgb(${accent} / 0.45)` : "var(--pg-card-edge)"}`,
            color: copied === "link" ? `rgb(${accent})` : "var(--pg-muted)",
            transition: "background 260ms ease, border-color 260ms ease, color 260ms ease, transform 200ms ease",
          }}
        >
          {copied === "link" ? (
            <Check className="h-4 w-4" strokeWidth={2.4} />
          ) : (
            <Link2 className="h-4 w-4" strokeWidth={1.75} />
          )}
        </button>
      </div>
    </div>
  )
}
